'use client';

import { useEffect, useRef } from 'react';
import { useConsent } from '@/core/components/providers/ConsentProvider';
import { trackSearch, trackViewItem, trackViewItemList } from '@/core/utils/analytics';

// Render-only trackers for the GA4/Ads/Meta browse events. They're components (not hooks) so the
// big list/page components can drop them in next to the data they already compute, after any
// early returns. Each fires once per distinct product/list/query and only with full consent —
// accepting the cookie banner on the page fires the pending event then.

function isBuilderPreview(): boolean {
    if (typeof window === 'undefined') return true;
    const path = window.location.pathname;
    return path.startsWith('/admin') || path.startsWith('/studio');
}

export function ViewItemTracker({ product, currency }: { product: any; currency: string }) {
    const { consent } = useConsent();
    const sentFor = useRef<string | null>(null);
    const key = product ? `${product.id}:${currency}` : null;

    useEffect(() => {
        if (consent !== 'all' || !key || sentFor.current === key || isBuilderPreview()) return;
        sentFor.current = key;
        trackViewItem(product, currency);
    }, [consent, key]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

export function ItemListTracker({
    products,
    listId,
    listName,
    currency,
}: {
    products: any[];
    listId: string;
    listName: string;
    currency: string;
}) {
    const { consent } = useConsent();
    const sentFor = useRef<string | null>(null);
    const key = products && products.length > 0 ? `${listId}:${currency}:${products.map((p) => p?.id).join(',')}` : null;

    useEffect(() => {
        if (consent !== 'all' || !key || sentFor.current === key || isBuilderPreview()) return;
        sentFor.current = key;
        trackViewItemList(products, listId, listName, currency);
    }, [consent, key]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

/** Fires `search` once the query has stayed unchanged for a second (live filter inputs type per key). */
export function SearchTracker({ query }: { query: string }) {
    const { consent } = useConsent();
    const lastSent = useRef<string>('');
    const term = (query || '').trim();

    useEffect(() => {
        if (consent !== 'all' || term.length < 2 || term === lastSent.current || isBuilderPreview()) return;
        const timer = setTimeout(() => {
            lastSent.current = term;
            trackSearch(term);
        }, 1000);
        return () => clearTimeout(timer);
    }, [consent, term]);

    return null;
}
